import {
  Link as ModeReactLink,
  LinkProps as ModeReactLinkProps,
} from '@stitch-fix/mode-react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { useRouter, NextRouter } from 'next/router';
import url, { UrlWithParsedQuery } from 'url';
import React, {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
} from 'react';
import { BRANCH_PREVIEW_PARAM } from '../../constants/branchPreview';
import logger from '../utils/logger';

export type LinkProps = Omit<ModeReactLinkProps, 'href'> &
  Pick<NextLinkProps, 'href' | 'replace' | 'scroll' | 'shallow' | 'prefetch'>;

// We only support the `ref` being placed on an anchor element (unlike the
// Mode React `Link`, which can also support buttons and other elements using `as`)
// because this component is only intended for true links (as opposed to buttons
// that just look like links).
type LinkRef = HTMLAnchorElement;

const processHrefQuery = (
  query: UrlWithParsedQuery['query'],
  branchPreviewValue?: string,
): UrlWithParsedQuery['query'] => {
  // Extract branch preview param specified on the passed in `href` because
  // this value should never be hardcoded.
  const { [BRANCH_PREVIEW_PARAM]: hardcodedBranchPreview, ...otherSearch } =
    query;

  if (hardcodedBranchPreview) {
    logger.warn(
      `Branch preview query param '${BRANCH_PREVIEW_PARAM}' was hardcoded to '${hardcodedBranchPreview}' in a Link and was removed . This value is automatically added to Links by sf-next when specified in the url and should never be hardcoded.`,
    );
  }

  if (!branchPreviewValue) {
    // If branch preview param is not set in the url, return the original search
    // minus any hardcoded branch preview values.
    return otherSearch;
  }

  // Return search with branch preview param added (or overridden if it was
  // hardcoded).
  return {
    ...otherSearch,
    [BRANCH_PREVIEW_PARAM]: branchPreviewValue,
  };
};

const getHrefString = (href: LinkProps['href']): string => {
  if (typeof href === 'string') {
    return href;
  }

  if (href.query && typeof href.query === 'string') {
    // There is an edge case in the url library where it does not handle
    // string-based `query` properly despite the types suggesting it is
    // supported. `query` is legacy and `search` is preferred and needs to
    // be a string, so we set that value instead to get the expected result.
    return url.format({
      ...href,
      query: undefined,
      search: href.query,
    });
  }

  return url.format(href);
};

/**
 * Processes the href to normalize urls. Processing includes:
 * - Persisting the branch preview query param.
 */
const processHref = (href: LinkProps['href'], router?: NextRouter) => {
  // Return early if next router is not set. We do this as a sensible fallback,
  // so consumers do not have to mock next router in all tests that use `Link`,
  // which would be tedious.
  if (!router || !href) {
    return href;
  }

  const branchPreviewRawValue = router.query[BRANCH_PREVIEW_PARAM];

  // Pick the first value if for some reason branch preview param has
  // accidentally been set twice in the url.
  const branchPreviewValue = Array.isArray(branchPreviewRawValue)
    ? branchPreviewRawValue[0]
    : branchPreviewRawValue;

  // We prefer the `url` library over using the browser `Url` because it is what
  // `next` uses under the hood, and it better handles relative urls, which is
  // the majority of what we will be handling in this code.
  //
  // Extracting `search` because we are modifying `query`, but `search`
  // trumps `query` if it is specified in `otherParsedUrl`.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { search, query, ...otherParsedUrl } = url.parse(
    // Normalize as a string to simplify handling of the many different
    // edge cases with the object format.
    getHrefString(href),
    true,
  );

  return url.format({
    ...otherParsedUrl,
    query: processHrefQuery(query, branchPreviewValue),
  });
};

// Explicitly creating this type and setting it as the return value for `Link`
// (instead of calculating it as a result of using `forwardRef`) prevents an
// error when attempting to build types.
// Exported variable 'Link' has or is using name 'Props' from external module "/Users/juliepagano/src/stitchfix/sf-next/node_modules/@stitch-fix/mode-react/types/Link/Link" but cannot be named.
type ForwardRefLink = ForwardRefExoticComponent<
  PropsWithoutRef<LinkProps> & RefAttributes<LinkRef>
>;

/**
 * A convenience component that wraps the Mode React `Link` component (that provides our look and feel) with Next's programmatic `Link` component (that handles client-side routing).
 *
 * @see https://nextjs.org/docs/api-reference/next/link
 */
const Link: ForwardRefLink = forwardRef<LinkRef, LinkProps>(
  ({ href, replace, scroll, shallow, prefetch, ...otherProps }, ref) => {
    const router = useRouter();

    return (
      <NextLink
        href={processHref(href, router)}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
        passHref
        legacyBehavior
      >
        <ModeReactLink {...otherProps} ref={ref} />
      </NextLink>
    );
  },
);

export default Link;
